import { NextResponse } from "next/server";

/**
 * Custom error class for API errors with status codes and error codes
 * 
 * @example
 * throw new APIError('User not found', 404, ErrorCodes.NOT_FOUND);
 * 
 * @example
 * throw new APIError('Validation failed', 400, ErrorCodes.VALIDATION_ERROR);
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number = 500,
    public code?: string
  ) {
    super(message);
    this.name = "APIError";
  }
}

export const ErrorCodes = {
  // Authentication errors
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  
  // Validation errors
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_INPUT: "INVALID_INPUT",
  MISSING_REQUIRED_FIELD: "MISSING_REQUIRED_FIELD",
  
  // Rate limiting
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  
  // Service errors
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  EXTERNAL_SERVICE_ERROR: "EXTERNAL_SERVICE_ERROR",
  
  // Resource errors
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  
  // Generic error
  INTERNAL_ERROR: "INTERNAL_ERROR",
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

interface ErrorResponse {
  error: string;
  code?: ErrorCode;
  details?: any;
  timestamp: string;
  path?: string;
}

/**
 * Creates a standardized error response
 * 
 * @param message - Error message for the response
 * @param statusCode - HTTP status code (default: 500)
 * @param code - Error code from ErrorCodes enum (optional)
 * @param details - Additional error details (only included in development)
 * @returns NextResponse with standardized error format
 * 
 * @example
 * return createErrorResponse('User not found', 404, ErrorCodes.NOT_FOUND);
 * 
 * @example
 * return createErrorResponse('Validation failed', 400, ErrorCodes.VALIDATION_ERROR, {
 *   fields: ['email', 'password']
 * });
 */
export function createErrorResponse(
  message: string,
  statusCode: number = 500,
  code?: ErrorCode,
  details?: any
): NextResponse<ErrorResponse> {
  const errorResponse: ErrorResponse = {
    error: message,
    code,
    details: process.env.NODE_ENV === "development" ? details : undefined,
    timestamp: new Date().toISOString(),
  };

  return NextResponse.json(errorResponse, { status: statusCode });
}

/**
 * Common error response helpers
 */
export const ErrorResponses = {
  unauthorized: (message = "Unauthorized") => 
    createErrorResponse(message, 401, ErrorCodes.UNAUTHORIZED),
  
  forbidden: (message = "Forbidden") => 
    createErrorResponse(message, 403, ErrorCodes.FORBIDDEN),
  
  notFound: (message = "Not found") => 
    createErrorResponse(message, 404, ErrorCodes.NOT_FOUND),
  
  validation: (message = "Validation error", details?: any) => 
    createErrorResponse(message, 400, ErrorCodes.VALIDATION_ERROR, details),
  
  rateLimit: (message = "Rate limit exceeded", retryAfter?: number) => {
    const response = createErrorResponse(message, 429, ErrorCodes.RATE_LIMIT_EXCEEDED);
    if (retryAfter) {
      response.headers.set("Retry-After", String(retryAfter));
    }
    return response;
  },
  
  internal: (message = "Internal server error", details?: any) => 
    createErrorResponse(message, 500, ErrorCodes.INTERNAL_ERROR, details),
  
  serviceUnavailable: (message = "Service temporarily unavailable") => 
    createErrorResponse(message, 503, ErrorCodes.SERVICE_UNAVAILABLE),
} as const;

/**
 * Wraps an API route handler with error handling
 * Catches errors and returns standardized error responses
 * 
 * @param handler - API route handler function to wrap
 * @returns Wrapped handler with error handling
 * 
 * @example
 * export const GET = withErrorHandler(async (request: Request) => {
 *   // Your API logic here
 *   return NextResponse.json({ data: 'success' });
 * });
 */
export function withErrorHandler<T extends Request = Request>(
  handler: (request: T) => Promise<Response>
) {
  return async (request: T): Promise<Response> => {
    try {
      return await handler(request);
    } catch (error) {
      logger.error("API route error:", error);
      
      if (error instanceof APIError) {
        return createErrorResponse(
          error.message,
          error.statusCode,
          error.code as ErrorCode
        );
      }
      
      if (error instanceof Error) {
        return ErrorResponses.internal(
          "An unexpected error occurred",
          process.env.NODE_ENV === "development" ? error.stack : undefined
        );
      }
      
      return ErrorResponses.internal("An unexpected error occurred");
    }
  };
}